import { del, get, patch, post } from './api'

/**
 * The Paradise Property Care client.
 *
 * Thin on purpose: the rules live on the server, where they cannot be skipped
 * by a stale tab. Prices in particular travel as STRINGS exactly as typed —
 * parsing happens once, at save, in shared/money.ts on the server. The client
 * never converts a price field to a number, which is the habit that made the
 * old input impossible to empty.
 */

export interface Species {
  id: string
  common_name: string
  scientific_name: string | null
  cultivar: string | null
  description: string | null
  care: string | null
  mature_size: string | null
  id_status: 'pending' | 'ai_suggested' | 'confirmed'
  plant_count?: number
}

export interface Plant {
  id: string
  code: string
  species_id: string | null
  nickname: string | null
  height_cm: number | null
  pot_size: string | null
  condition: string | null
  source: string | null
  acquired_on: string | null
  asking_price_cents: number | null
  cost_cents: number | null
  location: string | null
  availability: 'available' | 'reserved' | 'sold' | 'not_for_sale'
  is_batch: number
  quantity: number
  notes: string | null
  /** Present on listings: the cover photo, or the earliest one. */
  cover_upload_id?: string | null
  /** Present on listings: the linked species' common name. */
  common_name?: string | null
}

export interface Photo {
  id: string
  upload_id: string
  is_cover: number
  sort_order: number
  object_key: string
  content_type: string
}

export interface Show {
  id: string
  name: string
  venue: string | null
  starts_on: string | null
  ends_on: string | null
  status: 'planning' | 'open' | 'closed'
  item_count?: number
}

export interface ShowItem {
  id: string
  plant_id: string
  quantity: number
  packed: number
  code: string
  nickname: string | null
  asking_price_cents: number | null
  availability: string
  is_batch: number
  stock: number
  common_name: string | null
}

export interface Sale {
  id: string
  method: 'cash' | 'ath'
  status: 'awaiting' | 'paid' | 'refunded' | 'void'
  subtotal_cents: number
  discount_cents: number
  refund_cents: number
  total_cents: number
  note: string | null
  paid_at: number | null
  created_at: number
  lines?: { id: string; plant_id: string; code: string; nickname: string | null; quantity: number; unit_price_cents: number }[]
}

export interface PlanTask {
  id: string
  title: string
  detail: string | null
  category: 'setup' | 'meet' | 'prepare' | 'practice' | 'freeze' | 'general'
  day_offset: number
  assignee: string | null
  seed_key: string | null
  done_at: number | null
}

export interface Suggestion {
  commonName: string | null
  scientificName: string | null
  cultivar: string | null
  characteristics: string | null
  care: string | null
  matureSize: string | null
  confidence: string
  idStatus: 'ai_suggested'
  /**
   * Set when the model recognised this as a species the nursery ALREADY has.
   * Accepting then links the plant to that profile instead of creating a second
   * one under a slightly different spelling.
   */
  matchedSpeciesId: string | null
  matchedSpeciesName: string | null
  /** Where the size and characteristics came from, when they came from a book. */
  reference: { source: string; scientificName: string; corrected: boolean } | null
}

/** A palm from PalmTraits 1.0. */
export interface PalmMatch {
  scientificName: string
  genus: string | null
  heightM: number | null
  trunkCm: number | null
  layer: string | null
  habit: string[]
  fruit: string | null
  matureSize: string | null
  characteristics: string | null
}

export interface PalmPage {
  palms: PalmMatch[]
  /** How many matched the query — not how many are on this page. */
  matched: number
  /** How many palms there are altogether. */
  total: number
  source: string
}

/** Fields a plant form sends. Prices are strings; see the note above. */
export interface PlantDraft {
  speciesId?: string | null
  nickname?: string | null
  heightCm?: number | null
  potSize?: string | null
  condition?: string | null
  source?: string | null
  acquiredOn?: string | null
  askingPrice?: string
  cost?: string
  location?: string | null
  availability?: string
  isBatch?: boolean
  quantity?: number
  notes?: string | null
}

/* ── Plants ───────────────────────────────────────────────────────────────── */

export const listPlants = (params: { availability?: string; speciesId?: string; q?: string } = {}) => {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value) query.set(key, value)
  const suffix = query.toString() ? `?${query}` : ''
  return get<{ plants: Plant[] }>(`/api/plants${suffix}`).then((r) => r.plants)
}

export const getPlant = (id: string) =>
  get<{ plant: Plant; species: Species | null; photos: Photo[] }>(`/api/plants/${id}`)

export const createPlant = (draft: PlantDraft) =>
  post<{ plant: Plant }>('/api/plants', draft).then((r) => r.plant)

export const updatePlant = (id: string, draft: PlantDraft) =>
  patch<{ plant: Plant; photos: Photo[] }>(`/api/plants/${id}`, draft)

export const addPhotos = (plantId: string, uploadIds: string[]) =>
  post<{ photos: Photo[] }>(`/api/plants/${plantId}/photos`, { uploadIds }).then((r) => r.photos)

export const removePhoto = (plantId: string, photoId: string) =>
  del<{ photos: Photo[] }>(`/api/plants/${plantId}/photos/${photoId}`).then((r) => r.photos)

export const setCover = (plantId: string, photoId: string) =>
  post<{ photos: Photo[] }>(`/api/plants/${plantId}/cover`, { photoId }).then((r) => r.photos)

/* ── Species ──────────────────────────────────────────────────────────────── */

export const listSpecies = (q?: string) =>
  get<{ species: Species[] }>(`/api/species${q ? `?q=${encodeURIComponent(q)}` : ''}`).then((r) => r.species)

export const getSpecies = (id: string) =>
  get<{ species: Species; plants: Plant[]; photos: Photo[] }>(`/api/species/${id}`)

export const createSpecies = (body: Partial<Record<string, string>>) =>
  post<{ species: Species }>('/api/species', body).then((r) => r.species)

export const updateSpecies = (id: string, body: Record<string, unknown>) =>
  patch<{ species: Species }>(`/api/species/${id}`, body).then((r) => r.species)

/**
 * Search PalmTraits 1.0 — 2,557 palm species, measured and published.
 *
 * Server-side on purpose. The dataset is 400 kB and Dillon uses this outdoors
 * on mobile signal; ten matches over the wire beat the whole checklist in the
 * bundle.
 */
export const searchPalms = (q: string) =>
  get<{ palms: PalmMatch[]; total: number }>(`/api/palms?q=${encodeURIComponent(q)}`)
    .then((r) => r.palms)
    .catch(() => [] as PalmMatch[])

/** The whole checklist, for the reference table. Paged; the table asks for more. */
export const browsePalms = (params: {
  q?: string; sort?: string; dir?: string; limit?: number; offset?: number
}) => {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, String(value))
  }
  return get<PalmPage>(`/api/palms/browse?${query}`)
}

/**
 * Remove a species. The undo for adding one.
 *
 * The server refuses with 409 `species_in_use` if any plant points at it —
 * deleting would silently detach them, because the foreign key is SET NULL.
 * The caller shows that message rather than swallowing it.
 */
export const deleteSpecies = (id: string) => del<{ ok: true }>(`/api/species/${id}`)

export const addSpeciesPhotos = (id: string, uploadIds: string[]) =>
  post<{ photos: Photo[] }>(`/api/species/${id}/photos`, { uploadIds }).then((r) => r.photos)

export const removeSpeciesPhoto = (id: string, photoId: string) =>
  del<{ photos: Photo[] }>(`/api/species/${id}/photos/${photoId}`).then((r) => r.photos)

/* ── Shows ────────────────────────────────────────────────────────────────── */

export const listShows = () => get<{ shows: Show[] }>('/api/shows').then((r) => r.shows)

export const getShow = (id: string) =>
  get<{ show: Show; items: ShowItem[]; sales: Sale[] }>(`/api/shows/${id}`)

export const createShow = (body: { name: string; venue?: string; startsOn?: string }) =>
  post<{ show: Show }>('/api/shows', body).then((r) => r.show)

export const updateShow = (id: string, body: Record<string, unknown>) =>
  patch<{ show: Show }>(`/api/shows/${id}`, body)

export const selectForShow = (showId: string, plantIds: string[]) =>
  post<{ items: ShowItem[] }>(`/api/shows/${showId}/items`, { items: plantIds.map((plantId) => ({ plantId })) })

export const unselect = (showId: string, itemId: string) =>
  del<{ items: ShowItem[] }>(`/api/shows/${showId}/items/${itemId}`)

export const setPacked = (showId: string, itemId: string, packed: boolean) =>
  patch<{ items: ShowItem[] }>(`/api/shows/${showId}/items/${itemId}`, { packed })

export interface SellLine { plantId: string; quantity?: number; unitPrice?: string }

export const sell = (showId: string, body: {
  idempotencyKey: string; method: 'cash' | 'ath'; discount?: string; note?: string; lines: SellLine[]
}) => post<{ sale: Sale; duplicate?: boolean }>(`/api/shows/${showId}/sell`, body)

export const markSale = (saleId: string, status: 'paid' | 'awaiting' | 'void') =>
  patch<{ sale: Sale }>(`/api/sales/${saleId}`, { status })

export const refundSale = (saleId: string, amount: string) =>
  post<{ sale: Sale }>(`/api/sales/${saleId}/refund`, { amount })

export interface Summary {
  show: Show
  unitsSold: number
  unitsRemaining: number
  totals: { sales: number; gross_cents: number; discount_cents: number; refund_cents: number; net_cents: number }
  byMethod: { method: string; status: string; sales: number; total_cents: number }[]
}

export const showSummary = (showId: string) => get<Summary>(`/api/shows/${showId}/summary`)

/* ── The 15-day plan ──────────────────────────────────────────────────────── */

export const listPlan = () => get<{ tasks: PlanTask[] }>('/api/plan').then((r) => r.tasks)
export const getPlan = () => get<{ tasks: PlanTask[] }>('/api/plan')
export const seedPlan = () => post<{ tasks: PlanTask[] }>('/api/plan/seed', {}).then((r) => r.tasks)
export const setTaskDone = (id: string, done: boolean) =>
  patch<{ tasks: PlanTask[] }>(`/api/plan/${id}`, { done }).then((r) => r.tasks)

/* ── Identification ───────────────────────────────────────────────────────── */

export const identify = (uploadIds: string[]) =>
  post<{ suggestion: Suggestion }>('/api/identify', { uploadIds }).then((r) => r.suggestion)

/* ── Uploading a photo ────────────────────────────────────────────────────── */

/**
 * Send one photo to core's upload endpoint and return its id.
 *
 * Resized before it leaves the phone. A modern camera produces 4–12MB per
 * frame; three of those per plant, over a nursery's mobile signal, is the
 * difference between cataloguing a bench and giving up. 1600px on the long edge
 * is plenty for identification and for a tag photo.
 */
export async function uploadPhoto(file: File, purpose = 'plant-photo'): Promise<string> {
  const resized = await resize(file)
  const form = new FormData()
  form.append('file', resized, file.name || 'photo.jpg')
  form.append('purpose', purpose)

  const response = await fetch('/api/files', { method: 'POST', body: form, credentials: 'include' })
  if (!response.ok) throw new Error('That photo could not be uploaded.')
  const body = await response.json() as { upload?: { id: string }; id?: string }
  const id = body.upload?.id ?? body.id
  if (!id) throw new Error('The upload did not come back with an id.')
  return id
}

/*
 * 1600px on the long edge at q0.82.
 *
 * Not guessed — these are Abierto's measured numbers, from a real listing that
 * was serving a 4032x3024, 5.69MB camera JPEG as its card image: 5.69MB → 0.36MB,
 * a 16x reduction, with no visible loss at the size anyone actually views it.
 * A bench of two hundred plants at three photos each is the difference between
 * a usable nursery and a phone that gives up.
 */
const MAX_EDGE = 1600
const QUALITY = 0.82

export async function resize(file: File): Promise<Blob> {
  // A browser that cannot do this sends the original rather than failing: a big
  // upload beats no upload when Dillon is standing in front of the plant.
  if (typeof createImageBitmap !== 'function') return file
  try {
    /*
     * `imageOrientation: 'from-image'` is NOT optional.
     *
     * A phone writes portrait photos as landscape pixels plus an EXIF rotation
     * flag. Drawing the bitmap to a canvas throws that flag away, so without
     * this every portrait photo Dillon takes is silently stored on its side —
     * and it looks like a camera bug, not an upload bug, which is why it went
     * unnoticed in another app here for months.
     */
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))

    // Already small enough: send the original rather than re-encoding it, which
    // would only lose quality to no purpose.
    if (scale === 1) return file

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)

    return await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob ?? file), 'image/jpeg', QUALITY)
    })
  } catch {
    /*
     * Never block an upload. Every failure path here returns the original file.
     * A photo that is too big is a slow list; a photo that would not upload is
     * somebody who tried once and gave up.
     */
    return file
  }
}

/** Where a stored photo can be fetched from. Takes an upload id or a photo. */
export const photoUrl = (photo: Photo | string) =>
  `/api/files/${typeof photo === 'string' ? photo : photo.upload_id}`
